import { createStore, useStore } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import {
  createContext,
  useEffect,
  useContext,
  useRef,
  type ReactNode,
} from 'react'
import { isMatching, P } from 'ts-pattern'
import { nanoid } from 'nanoid'
import { dbVersion } from './db'
import type { StateCreator, StoreApi } from 'zustand'
import { useChartStore } from '@/stores/chart'
import * as api from '@/lib/api'

const PObject = P.when(
  (v) => v !== null && typeof v === 'object' && Object.keys(v).length > 0
)

const isSessionAuthenticated = isMatching({
  userId: P.string,
  id: P.string,
  email: P.optional(P.string),
})

const isSessionAvailable = isMatching({
  userId: P.optional(P.nullish),
  id: P.string,
})

const isProfileAvailable = isMatching({
  currentId: P.string,
  profiles: PObject,
})

interface IAuthError {
  code: number
  message?: string
}

interface IUserContextState {
  profiles: Record<string, { id?: string; name: string }>
  profileId: string | undefined
  sessionId: string | undefined
  changeProfile: (id: string) => Promise<void>
  removeProfile: (id: string) => Promise<void>
  addProfile: (name: string, id?: string) => Promise<void>
  userId: string | undefined
  email: string | undefined
  signup: (email: string, passw: string) => Promise<void>
  login: (email: string, passw: string) => Promise<void | IAuthError>
  logout: () => Promise<void>
  bootstrap: () => Promise<void>
  settings: Record<string, unknown>
  set: (key: string, value: unknown) => void
}

const buildStore: StateCreator<IUserContextState> = (set, get) => {
  const signup: IUserContextState['signup'] = async (email, passw) => {
    return api
      .signup(email, passw)
      .then(async () => {
        return api.getSession().catch(() => api.startSession())
      })
      .then((session) => {
        set({
          sessionId: session.id,
          userId: session.userId,
        })

        if (session.userId) {
          return api.addProfile(session.id, 'Default')
        } else {
          throw new Error('Error authenticating.')
        }
      })
      .then(async () => {
        return api.getProfile().then((res) => {
          set({
            profileId: res.currentId,
            profiles: Object.assign({}, res.profiles as unknown),
          })
        })
      })
  }

  async function bootstrap() {
    Promise.all([
      api.getSession().catch(() => null),
      api.getProfile().catch(() => null),
    ]).then(([session, profile]) => {
      const state = get()

      if (isSessionAuthenticated(session) && isProfileAvailable(profile)) {
        console.log('using authenticated session and available profiles')
        set({
          sessionId: session.id,
          email: session.email,
          userId: session.userId,
          profileId: profile.currentId,
          profiles: profile.profiles,
        })
      } else if (
        isSessionAuthenticated(session) &&
        !isProfileAvailable(profile)
      ) {
        console.log('using authenticated session with no available profiles')
        set({
          sessionId: session.id,
          email: session.email,
          userId: session.userId,
          profileId: session.id,
          profiles: {
            ...state.profiles,
            [session.id]: {
              id: session.id,
              name: 'Default',
            },
          },
        })
      } else if (isSessionAvailable(session) && !isProfileAvailable(profile)) {
        console.log('using basic session with no available profiles')
        set({
          email: session.email,
          userId: session.userId,
          sessionId: session.id,
          profileId: session.id,
        })
      } else {
        console.log('building new session and profile')
        api.startSession().then((session) => {
          set({
            email: session.email,
            userId: session.userId,
            sessionId: session.id,
            profileId: session.id,
          })
        })
      }
    })
  }

  const login: IUserContextState['login'] = async (email, passw) => {
    return api
      .login(email, passw)
      .then(async () => {
        return api.getSession().catch(() => api.startSession())
      })
      .then(async (session) => {
        set({
          sessionId: session.id,
          userId: session.userId,
          email: session.email,
        })

        return api.getProfile().then((res) => {
          const curr = get()
          const tempName = nanoid(6)
          const nextProfiles = Object.assign(
            {},
            res.profiles
          ) as unknown as typeof curr.profiles
          let swap = false

          if (
            !!res.currentId &&
            curr.profileId &&
            curr.profileId !== res.currentId &&
            dbVersion > 0
          ) {
            console.dir({ dbVersion })
            const save = window.confirm(
              'You have made unsynced changes. Do you want to save them to a profile?'
            )
            if (save) {
              api.addProfile(curr.profileId, nanoid(6))
              nextProfiles[curr.profileId] = {
                id: curr.profileId,
                name: tempName,
              }
              swap = window.confirm(
                'Do you want to continue using this profile?'
              )
            }
          }

          set({
            profiles: nextProfiles,
            profileId: !swap ? res.currentId : curr.profileId,
          })
        })
      })
  }

  const logout: IUserContextState['logout'] = async () => {
    api
      .logout()
      .then(() => {
        return api.startSession()
      })
      .then((newSession) => {
        console.log('wiping session for logout')
        set({
          userId: undefined,
          email: undefined,
          profileId: newSession.id,
          sessionId: newSession.id,
          profiles: {},
        })
        useChartStore.persist.clearStorage()
      })
      .catch(() => {
        console.error('encountered error during logout; wiping store')
        set({
          userId: undefined,
          email: undefined,
          profileId: undefined,
          sessionId: undefined,
          profiles: {},
        })
        window.location.reload()
      })
  }

  const changeProfile: IUserContextState['changeProfile'] = async (id) => {
    set({
      profileId: id,
    })
  }

  const removeProfile: IUserContextState['removeProfile'] = async (id) => {
    api.removeProfile(id).then(() => {
      const state = get()
      const profiles = state.profiles
      delete profiles[id]
      const profileId = Object.keys(profiles)[0]
      set({
        profileId,
        profiles,
      })
    })
  }

  const addProfile: IUserContextState['addProfile'] = async (
    name,
    id = nanoid()
  ) => {
    api.addProfile(id, name).then(() => {
      const state = get()
      const profiles = state.profiles
      profiles[id] = { id, name }
      set({
        profileId: id,
        profiles,
      })
    })
  }

  return {
    sessionId: undefined,
    profiles: {},
    profileId: undefined,
    userId: undefined,
    email: undefined,
    changeProfile,
    removeProfile,
    addProfile,
    signup,
    login,
    logout,
    bootstrap,
    settings: {},
    set: (key: string, value: unknown) => {
      set((c) => ({
        settings: {
          ...c.settings,
          [key]: value,
        },
      }))
    },
  }
}

function buildPersistedStore() {
  return createStore(
    persist(buildStore, {
      version: 3,
      storage: createJSONStorage(() => localStorage),
      name: 'dumblift_user',
      partialize: (state) => {
        return {
          profiles: state.profiles,
          profileId: state.profileId,
          sessionId: state.sessionId,
          userId: state.userId,
          settings: state.settings,
        }
      },
    })
  )
}

type PersistedStore = ReturnType<typeof buildPersistedStore>
export const UserContext = createContext<PersistedStore | undefined>(undefined)

type Props = {
  children:
    | ((store: StoreApi<PersistedStore>) => ReactNode)
    | ReactNode
    | JSX.Element
}
export const UserProvider = ({ children }: Props) => {
  const store = useRef<PersistedStore | undefined>(
    buildPersistedStore()
  ).current

  useEffect(() => {
    if (!store) {
      return
    }

    Promise.all([
      api.getSession().catch(() => null),
      api.getProfile().catch(() => null),
    ]).then(([session, profile]) => {
      const state = store.getState()

      if (isSessionAuthenticated(session) && isProfileAvailable(profile)) {
        console.log('using authenticated session and available profiles')
        store.setState({
          sessionId: session.id,
          email: session.email,
          userId: session.userId,
          profileId: profile.currentId,
          profiles: profile.profiles,
        })
      } else if (
        isSessionAuthenticated(session) &&
        !isProfileAvailable(profile)
      ) {
        store.setState({
          sessionId: session.id,
          email: session.email,
          userId: session.userId,
          profileId: session.id,
          profiles: {
            ...state.profiles,
            [session.id]: {
              id: session.id,
              name: 'Default',
            },
          },
        })
      } else if (
        isSessionAvailable(session) &&
        !isProfileAvailable(profile) &&
        state.profileId
      ) {
        store.setState({
          email: session.email,
          userId: session.userId,
          sessionId: session.id,
          profileId: state.profileId,
          profiles: {
            [state.profileId]: {
              id: state.profileId,
              name: 'Default',
            },
          },
        })
      } else {
        api.startSession().then((session) => {
          store.setState({
            email: session.email,
            userId: session.userId,
            sessionId: session.id,
            profileId: session.id,
          })
        })
      }
    })
    ;(window as unknown as { store: unknown }).store = store
  }, [store])

  return (
    <UserContext.Provider value={store}>
      {typeof children === 'function' ? children(store as any) : children}
    </UserContext.Provider>
  )
}

export const useUser = <R = unknown,>(
  selector: (state: IUserContextState) => R
): R => {
  const store = useContext(UserContext)
  if (!store) {
    throw new Error('Missing UserProvider!')
  }
  return useStore(store, selector)
}
